import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/auth-context.js";
import { useT, type Translate } from "../../i18n/index.js";
import type { ProvidersResponse } from "./types.js";

interface Props {
  registrationMode: ProvidersResponse["registrationMode"];
}

const s = {
  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "var(--space-md)",
  },
  label: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.375rem",
    fontSize: "var(--text-sm)",
    fontWeight: 600,
    color: "var(--text-mid)",
  },
  input: {
    padding: "0.875rem var(--space-md)",
    fontSize: "var(--text-base)",
    borderRadius: "var(--radius-md)",
    borderWidth: "2px",
    borderStyle: "solid",
    borderColor: "var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    width: "100%",
    outline: "none",
    transition: "border-color var(--duration-fast) var(--ease-out)",
  },
  inputFocus: {
    borderColor: "var(--brand)",
  },
  btn: {
    padding: "1rem",
    fontSize: "var(--text-base)",
    fontWeight: 700,
    cursor: "pointer",
    borderRadius: "var(--radius-md)",
    background: "var(--brand)",
    color: "#fff",
    borderWidth: 0,
    borderStyle: "none",
    borderColor: "transparent",
    minHeight: "58px",
    marginTop: "var(--space-xs)",
    transition: "background var(--duration-fast) var(--ease-out)",
  },
  error: {
    color: "var(--error)",
    fontSize: "var(--text-sm)",
    margin: 0,
    padding: "0.75rem 1rem",
    background: "var(--error-lt)",
    borderRadius: "var(--radius-sm)",
    fontWeight: 500,
  },
  linkRow: {
    textAlign: "center" as const,
    margin: 0,
    fontSize: "var(--text-sm)",
    color: "var(--text-mid)",
  },
};

type ValidationDetails = {
  fieldErrors?: Record<string, string[] | undefined>;
};

function getValidationMessage(details: unknown, t: Translate): string {
  const fieldErrors = (details as ValidationDetails | undefined)?.fieldErrors;

  if (fieldErrors?.["email"]?.length) {
    return t("auth.invalidEmail");
  }
  if (fieldErrors?.["password"]?.length) {
    return t("auth.passwordMin");
  }
  if (fieldErrors?.["displayName"]?.length) {
    return t("auth.enterDisplayName");
  }
  if (fieldErrors?.["inviteCode"]?.length) {
    return t("auth.enterInviteCode");
  }

  return t("auth.checkForm");
}

function getRegistrationErrorMessage(err: unknown, t: Translate): string {
  if (!(err instanceof ApiError)) {
    return t("auth.registrationFailed");
  }

  if (err.code === "VALIDATION_ERROR" || err.status === 400) {
    return getValidationMessage(err.details, t);
  }
  if (err.code === "CONFLICT" || err.status === 409) {
    return t("auth.accountExists");
  }
  if (err.code === "FORBIDDEN" || err.status === 403) {
    if (err.message.toLowerCase().includes("invite")) {
      return t("auth.inviteInvalid");
    }
    return t("auth.registrationDisabled");
  }
  if (err.status >= 500) {
    return t("auth.serverRegistrationFailed");
  }

  return err.message || t("auth.registrationFailed");
}

export function RegisterForm({ registrationMode }: Props) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);
  const { refetch } = useAuth();
  const navigate = useNavigate();

  if (registrationMode === "closed") {
    return (
      <p
        style={{ textAlign: "center", color: "var(--text-mid)", fontSize: "var(--text-sm)" }}
        className="auth-link-row"
      >
        {t("auth.registrationDisabled")} {" "}
        <Link to="/login" className="auth-inline-link">
          {t("auth.signIn")}
        </Link>
      </p>
    );
  }

  const inputStyle = (name: string) => ({
    ...s.input,
    ...(focused === name ? s.inputFocus : {}),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    const trimmedDisplayName = displayName.trim();
    const trimmedInviteCode = inviteCode.trim();

    if (!trimmedEmail) {
      setError(t("auth.enterEmail"));
      return;
    }
    if (!trimmedDisplayName) {
      setError(t("auth.enterDisplayName"));
      return;
    }
    if (password.length < 8) {
      setError(t("auth.passwordMin"));
      return;
    }
    if (registrationMode === "invite" && !trimmedInviteCode) {
      setError(t("auth.enterInviteCode"));
      return;
    }

    setIsSubmitting(true);
    try {
      const body: Record<string, string> = {
        email: trimmedEmail,
        displayName: trimmedDisplayName,
        password,
      };
      if (registrationMode === "invite") {
        body.inviteCode = trimmedInviteCode;
      }
      await api.post("/auth/register", body);
      await refetch();
      navigate("/admin/quizzes");
    } catch (err) {
      setError(getRegistrationErrorMessage(err, t));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={s.form}>
      {error && (
        <p role="alert" style={s.error}>
          {error}
        </p>
      )}
      <label style={s.label}>
        {t("auth.email")}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onFocus={() => setFocused("email")}
          onBlur={() => setFocused(null)}
          required
          autoComplete="email"
          style={inputStyle("email")}
        />
      </label>
      <label style={s.label}>
        {t("auth.displayName")}
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          onFocus={() => setFocused("displayName")}
          onBlur={() => setFocused(null)}
          required
          autoComplete="nickname"
          style={inputStyle("displayName")}
        />
      </label>
      <label style={s.label}>
        {t("auth.password")}
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onFocus={() => setFocused("password")}
          onBlur={() => setFocused(null)}
          required
          autoComplete="new-password"
          minLength={8}
          style={inputStyle("password")}
        />
      </label>
      {registrationMode === "invite" && (
        <label style={s.label}>
          {t("auth.inviteCode")}
          <input
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            onFocus={() => setFocused("inviteCode")}
            onBlur={() => setFocused(null)}
            required
            autoComplete="off"
            style={inputStyle("inviteCode")}
          />
        </label>
      )}
      <button
        type="submit"
        className="btn-tactile"
        disabled={isSubmitting}
        style={{
          ...s.btn,
          opacity: isSubmitting ? 0.7 : 1,
          cursor: isSubmitting ? "not-allowed" : "pointer",
        }}
      >
        {isSubmitting ? t("auth.creatingAccount") : t("auth.createAccount")}
      </button>
      <p style={s.linkRow} className="auth-link-row">
        {t("auth.alreadyAccount")} {" "}
        <Link to="/login" className="auth-inline-link">
          {t("auth.signIn")}
        </Link>
      </p>
    </form>
  );
}
